// Make changes › + Add a field here / + Add a column — a new field in plain
// words: what it is called, what people enter (kind tiles), its choices or
// the screen it is picked from, whether it must be filled in, which section.
// Added at once (one insert = one undo step); its ⚙ opens next for rules and
// checks. The stored name is made from the field's name (fieldKinds.
// storedName), unique on the form.

import { useEffect, useMemo, useState } from 'react'
import type { ColumnDef, EditMaskValue, SqlModel } from '../txn/types'
import { groupColumns } from '../txn/FormRenderer'
import type { CustomizeSession } from './session'
import type { BuildOps, BuildSources } from './build/types'
import { columnKey, formBase, groupKey } from './txnOps'
import { CenterDialog, ChipGroup, PanelNote, PanelSelect, VIOLET, VIOLET_DARK, VIOLET_LINE, VioletButton } from './ui'
import { FIELD_KINDS, FIELD_SUGGESTIONS, kindById, newColumn, type LookupSpec } from './build/fieldKinds'
import { tw } from '../testHooks'
import { Loader2 } from 'lucide-react'

export interface AddFieldDialogProps {
  session: CustomizeSession
  /** The screen's name (its header form's title). */
  screenTitle: string
  form: SqlModel
  formNo: string
  /** The header form: sections are offered; a line form has none. */
  isHeader: boolean
  /** The section it was asked from (null = the first). */
  group: string | null
  transport: BuildOps
  onClose: () => void
  /** The new field's key, once stored (its ⚙ opens next). */
  onAdded: (colKey: string) => void
}

export default function AddFieldDialog({
  session, screenTitle, form, formNo, isHeader, group, transport, onClose, onAdded,
}: AddFieldDialogProps) {
  const cols = useMemo(() => (form.columns || []).map((w) => w.column).filter(Boolean) as ColumnDef[], [form])
  const groups = useMemo(() => groupColumns(cols, false, [], form.group_settings).map(([g]) => g), [cols, form.group_settings])
  const [label, setLabel] = useState('')
  const [kindId, setKindId] = useState('words')
  const [choices, setChoices] = useState<EditMaskValue[]>([])
  const [choiceDraft, setChoiceDraft] = useState('')
  const [from, setFrom] = useState('')
  const [mandatory, setMandatory] = useState(false)
  const [section, setSection] = useState(groupKey(group ?? groups[0] ?? ''))
  const [sources, setSources] = useState<BuildSources | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    transport.sources().then((s) => { if (!cancelled) setSources(s) })
      .catch(() => { if (!cancelled) setSources({ screens: [], reports: [] }) })
    return () => { cancelled = true }
  }, [transport])

  const kind = kindById(kindId)!
  const screen = sources?.screens.find((s) => s.obj_name === from)
  const lookup: LookupSpec | null = screen ? {
    table: screen.table,
    data: screen.key_column || screen.columns[0]?.db_name || '',
    display: screen.columns.find((c) => !c.key && /name|title|descr/i.test(c.db_name))?.db_name
      || screen.columns.find((c) => !c.key)?.db_name || screen.key_column,
  } : null
  const ready = !!label.trim() && (kindId !== 'pick' || !!lookup) && (kindId !== 'choice' || choices.length > 0)

  const addChoice = () => {
    const t = choiceDraft.trim()
    if (!t || choices.some((c) => c.display.toLowerCase() === t.toLowerCase())) return
    setChoices([...choices, { data: t, display: t }])
    setChoiceDraft('')
  }

  const add = async () => {
    if (!ready || busy) return
    setBusy(true)
    setError(null)
    // At the end of its section; the list shows it last.
    const rawGroup = cols.find((c) => groupKey(c.group_name) === section)?.group_name ?? (section === 'Others' ? '' : section)
    const inGroup = cols.filter((c) => groupKey(c.group_name) === section)
    const y = Math.max(0, ...inGroup.map((c) => Number(c.y) || 0), ...(inGroup.length ? [] : cols.map((c) => Number(c.y) || 0))) + 1
    const x = Math.max(0, ...cols.map((c) => Number(c.x) || 0)) + 10
    const col = newColumn({
      label, kindId, table: String(form.main_table || ''), group: isHeader ? (rawGroup || '') : '',
      taken: cols.flatMap((c) => [c.db_name, c.col_id, c.name].filter(Boolean) as string[]),
      mandatory, values: kindId === 'choice' ? choices : undefined, lookup, y, x,
    })
    if (kindId === 'pick' && screen) col.lookup_add_object = screen.obj_name
    try {
      const ok = await session.apply({
        set: [], reset: [],
        ops: [{ op: 'insert', array: `${formBase(formNo)}.columns`, item: col }],
      }, `Added ${label.trim()}`)
      if (ok) onAdded(columnKey(col as unknown as ColumnDef))
      else setError(session.errors.join(' · ') || 'The field was not added.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <CenterDialog title={isHeader ? `Add a field to ${screenTitle}` : `Add a column to ${form.form_title || 'the lines'}`}
      onClose={onClose} hook="make-changes-add-field"
      footer={(
        <>
          <span style={{ flex: '1 1 260px', fontSize: 13, color: 'var(--color-text-secondary, #6b7280)' }}>
            Added at once. Its ⚙ opens next, for rules and checks.
          </span>
          <VioletButton quiet onClick={onClose}>Cancel</VioletButton>
          <VioletButton onClick={() => { void add() }} disabled={!ready || busy} hook="make-changes-add-field-confirm">
            {busy && <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} />}Add field
          </VioletButton>
        </>
      )}>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <span style={{ fontWeight: 600 }}>1. What is it called?</span>
        <input autoFocus value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Trip number"
          onKeyDown={(e) => { if (e.key === 'Enter') void add() }}
          {...tw('field', 'make-changes-new-field-name')}
          style={{ height: 36, borderRadius: 6, border: '1px solid var(--color-border, #d5dae1)', padding: '0 10px', fontSize: 15, fontFamily: 'inherit' }} />
      </label>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
        <span style={{ fontSize: 13, color: 'var(--color-text-secondary, #6b7280)' }}>Often added:</span>
        {FIELD_SUGGESTIONS.filter((s) => !cols.some((c) => String(c.heading || '').toLowerCase() === s.label.toLowerCase()))
          .map((s) => (
            <button key={s.label} type="button" onClick={() => { setLabel(s.label); setKindId(s.kind) }}
              style={{ minHeight: 28, padding: '0 10px', borderRadius: 14, border: `1px solid ${VIOLET_LINE}`, background: '#fff', color: VIOLET_DARK, cursor: 'pointer', fontSize: 13, fontFamily: 'inherit' }}>
              {s.label}
            </button>
          ))}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <span style={{ fontWeight: 600 }}>2. What will people enter?</span>
        <div role="radiogroup" aria-label="What people enter" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 8 }}>
          {FIELD_KINDS.map((k) => {
            const on = k.id === kindId
            return (
              <button key={k.id} type="button" role="radio" aria-checked={on} onClick={() => setKindId(k.id)}
                {...tw('action', `make-changes-kind:${k.id}`)}
                style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, padding: '8px 10px',
                  borderRadius: 8, cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
                  border: `1px solid ${on ? VIOLET : 'var(--color-border, #d5dae1)'}`,
                  background: on ? '#f5f3ff' : 'var(--color-bg, #fff)', color: 'var(--color-text, #111827)',
                }}>
                <strong style={{ fontSize: 14, color: on ? VIOLET_DARK : undefined }}>{k.label}</strong>
                <span style={{ fontSize: 13, color: 'var(--color-text-secondary, #6b7280)' }}>{k.line}</span>
              </button>
            )
          })}
        </div>
      </div>
      {kindId === 'choice' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ fontWeight: 600 }}>3. What are the choices?</span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {choices.map((c) => (
              <span key={c.data} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, minHeight: 30, padding: '0 4px 0 10px', borderRadius: 15, background: '#ede9fe', color: VIOLET_DARK, fontWeight: 500 }}>
                {c.display}
                <button type="button" aria-label={`Remove ${c.display}`} onClick={() => setChoices(choices.filter((x) => x.data !== c.data))}
                  style={{ border: 0, background: 'transparent', color: VIOLET_DARK, cursor: 'pointer', fontSize: 15 }}>×</button>
              </span>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <input value={choiceDraft} onChange={(e) => setChoiceDraft(e.target.value)} placeholder="Type a choice"
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addChoice() } }}
              {...tw('field', 'make-changes-new-choice')}
              style={{ flex: 1, height: 32, borderRadius: 6, border: '1px solid var(--color-border, #d5dae1)', padding: '0 10px', fontFamily: 'inherit' }} />
            <VioletButton quiet onClick={addChoice} hook="make-changes-new-choice-add">Add</VioletButton>
          </div>
        </div>
      )}
      {kindId === 'pick' && (
        <PanelSelect label="3. Picked from which screen?" value={from} onChange={setFrom}
          options={[{ value: '', label: sources ? 'Choose a screen…' : 'Loading…' },
            ...(sources?.screens || []).map((s) => ({ value: s.obj_name, label: s.title }))]} />
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ fontWeight: 600 }}>Must people fill it in?</span>
          <ChipGroup label="Must people fill it in" hook="make-changes-new-required" value={mandatory ? 'yes' : 'no'}
            options={[{ value: 'no', label: 'No' }, { value: 'yes', label: 'Yes' }]} onChange={(v) => setMandatory(v === 'yes')} />
        </div>
        {isHeader && groups.length > 1 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontWeight: 600 }}>In which section?</span>
            <ChipGroup label="In which section" hook="make-changes-new-section" value={section}
              options={groups.map((g) => ({
                value: groupKey(g),
                label: String(form.group_settings?.[groupKey(g)]?.label ?? '').trim() || (g === 'Others' ? 'Other information' : g),
              }))} onChange={setSection} />
          </div>
        )}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: 12, borderRadius: 8, background: 'var(--color-bg-secondary, #f6f7f9)' }}>
        <span style={{ fontSize: 13, color: 'var(--color-text-secondary, #6b7280)' }}>People will see</span>
        <span style={{ fontWeight: 600 }}>{label.trim() || 'Your field'}{mandatory && <span style={{ color: '#b42318' }}> *</span>}</span>
        <span style={{
          height: 34, borderRadius: 6, border: '1px solid var(--color-border, #d5dae1)', background: 'var(--color-bg, #fff)',
          display: 'flex', alignItems: 'center', padding: '0 10px', color: 'var(--color-text-secondary, #6b7280)', fontSize: 14,
        }}>
          {kindId === 'choice' ? (choices[0]?.display ? `${choices[0].display} ▾` : 'Pick one ▾')
            : kindId === 'pick' ? (screen ? `Pick from ${screen.title}…` : 'Pick…')
            : kindId === 'yesno' ? '☐' : kind.line}
        </span>
      </div>
      {error && <PanelNote tone="warn">Not added. {error}</PanelNote>}
      {busy && <span style={{ fontSize: 13, color: VIOLET }}>Adding it to the screen and its table…</span>}
    </CenterDialog>
  )
}
